import { gql } from '@apollo/client';
import { Form, Select, Input, Alert, Row, InputNumber, Col, Checkbox, Button } from 'antd';
import { useLazyQuery, useMutation, useQuery } from 'hooks/apollo';
import useUser from 'hooks/useUser';
import useExternalAIConfirmation from 'hooks/useExternalAIConfirmation';
import React, { useEffect, useState } from 'react';
import { DEFAULT_MODEL, MODELS, SUGGEST_TAGS_SYSTEM_PROMPT } from 'shared/constants/ai';

const { TextArea } = Input;

const autoTagDefaultValues = {
  min: 3,
  max: 15,
  modelName: DEFAULT_MODEL,
  systemPrompt: SUGGEST_TAGS_SYSTEM_PROMPT,
};

const SUGGEST_TAGS = gql`
  query($input: AutoSuggestTagsInput!) {
    autoSuggestTags(input: $input) {
      name
      relevance
      frequency
    }
  }
`;

const CREATE_TAGS = gql`
  mutation($input: AddThemesInput!) {
    addThemes(input: $input) {
      id
    }
  }
`;

const GET_AI_DEFAULTS = gql`
  query($keys: [String!]) {
    settings(keys: $keys)
  }
`;

const AutoSuggestTags = ({
  tags,
  onDone,
  questionIds,
  comments,
  questionTitle,
}) => {
  const { isSuperUser } = useUser();
  const confirmExternalAI = useExternalAIConfirmation();
  const [suggestForm] = Form.useForm();
  const [createForm] = Form.useForm();
  const [suggestTags, { loading }] = useLazyQuery(SUGGEST_TAGS);
  const [createTags, { loading: creating }] = useMutation(CREATE_TAGS);
  const { data: aiDefaultsData } = useQuery(GET_AI_DEFAULTS, {
    variables: { keys: ['aiDefaultModel', 'aiCategorySystemPrompt'] },
    skip: !isSuperUser,
  });
  const [suggestions, setSuggestions] = useState([]);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  const selectedTags = Form.useWatch('suggestions', createForm);

  const defaultModelName = aiDefaultsData?.settings?.aiDefaultModel || DEFAULT_MODEL;
  const defaultSystemPrompt = aiDefaultsData?.settings?.aiCategorySystemPrompt || SUGGEST_TAGS_SYSTEM_PROMPT;

  useEffect(() => {
    if (!isSuperUser) {
      return;
    }

    if (!suggestForm.getFieldValue('modelName')) {
      suggestForm.setFieldValue('modelName', defaultModelName);
    }

    if (!suggestForm.getFieldValue('systemPrompt')) {
      suggestForm.setFieldValue('systemPrompt', defaultSystemPrompt);
    }
  }, [isSuperUser, defaultModelName, defaultSystemPrompt, suggestForm]);

  const initialValues = {
    ...autoTagDefaultValues,
    tagIds: tags.map((tag) => tag.id),
    modelName: defaultModelName,
    systemPrompt: defaultSystemPrompt,
  };

  const onSuggestTags = async (values) => {
    if (!await confirmExternalAI()) return;
    const input = {
      questionIds,
      min: values.min,
      max: values.max,
      existingTags: values.tagIds.map((tagId) => tags.find((tag) => tag.id === tagId)?.text),
      exampleResponses: comments.slice(0, 100).map((comment) => comment?.text),
      questionTitle,
      modelName: values.modelName,
      systemPrompt: values.systemPrompt,
      customPrompt: values.customPrompt,
    };
    const result = await suggestTags({ variables: { input } });

    setSuggestions(result.data.autoSuggestTags.toSorted((a, b) => b.relevance - a.relevance));
  };

  const onCreateTags = async (values) => {
    await createTags({ variables: { input: { themes: values.suggestions, questionIds } } });
    onDone();
  };

  return (
    <Row gutter={16}>
      <Col span={12}>
        <Form form={suggestForm} layout="vertical" initialValues={initialValues} onFinish={onSuggestTags}>
          <Form.Item>
            <Alert message="This will send some survey responses to an external service. Select existing tags to use as examples." type="warning" showIcon />
          </Form.Item>
          <Form.Item name="tagIds" label="Existing tags">
            <Select options={tags.map((theme) => ({ value: theme.id, label: theme.text }))} mode="multiple" allowClear />
          </Form.Item>
          <Row gutter={16}>
            <Col>
              <Form.Item name="min" label="Min tags">
                <InputNumber min={1} max={15} />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="max" label="Max tags">
                <InputNumber min={1} max={15} />
              </Form.Item>
            </Col>
          </Row>
          {isSuperUser && (
            <Form.Item>
              <Button type="link" style={{ paddingLeft: 0 }} onClick={() => setShowAdvancedSettings((prev) => !prev)}>
                {showAdvancedSettings ? 'Hide advanced settings' : 'Show advanced settings'}
              </Button>
              {showAdvancedSettings && (
                <>
                  <Form.Item name="modelName" label="Model">
                    <Select
                      options={MODELS.map((model) => ({
                        label: model === defaultModelName ? `${model} (default)` : model,
                        value: model,
                      }))}
                    />
                  </Form.Item>
                  <Form.Item name="systemPrompt" label="System prompt override">
                    <TextArea autoSize={{ minRows: 3 }} />
                  </Form.Item>
                  <Form.Item name="customPrompt" label="Custom instructions">
                    <TextArea autoSize={{ minRows: 3 }} />
                  </Form.Item>
                </>
              )}
            </Form.Item>
          )}

          <Button htmlType="submit" type="primary" loading={loading || creating}>
            Generate suggestions
          </Button>
        </Form>
      </Col>
      <Col span={12}>
        {suggestions.length > 0 && (
          <Form form={createForm} layout="vertical" onFinish={onCreateTags}>
            <Form.Item label="Suggestions (occurence)" name="suggestions">
              <Checkbox.Group className="checkbox-group-vertical" options={suggestions.map((suggestion) => ({ label: `${suggestion.name}  (${suggestion.frequency})`, value: suggestion.name }))} />
            </Form.Item>
            <Button htmlType="submit" type="primary" loading={creating} disabled={!selectedTags?.length}>Create selected tags</Button>
          </Form>
        )}
      </Col>
    </Row>
  );
};

export default AutoSuggestTags;
